import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Skeleton,
    TextField,
    Grid,
    Typography,
    Autocomplete,
} from '@mui/material';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import axios from 'axios';
import useAuthorize from '../hooks/useAuthorize';
import { MARKETING_DASHBOARD_ROLES } from '../roles/portalRoles';
import { AnalyticsFreshnessNote } from '../utils/AnalyticsFreshnessNote';
import FunnelTrendChart from './FunnelTrendChart';
import FunnelSummaryChart from './FunnelSummaryChart';
import CustomerProfileFunnelSummaryChart from './CustomerProfileFunnelSummaryChart';
import CustomerProfileFunnelTrendChart from './CustomerProfileFunnelTrendChart';

type FunnelDefinition = {
    label: string;
    description: string;
};

type CommerceFunnelTotals = {
    visits: number;
    wishlist: number;
    registered: number;
    inquiries: number;
    orders: number;
};

const emptyCommerceFunnelTotals: CommerceFunnelTotals = {
    visits: 0,
    wishlist: 0,
    registered: 0,
    inquiries: 0,
    orders: 0,
};

const commerceFunnelDefinitions: FunnelDefinition[] = [
    {
        label: 'Visits',
        description: 'Distinct website visitors who viewed at least one page in the selected date range.',
    },
    {
        label: 'Wishlist',
        description: 'Distinct visitors who added or saved a product to their wishlist in the selected date range.',
    },
    {
        label: 'Registered',
        description: 'Distinct visitors who became registered customers in the selected date range.',
    },
    {
        label: 'Inquiry',
        description: 'Distinct visitors who placed a product inquiry in the selected date range.',
    },
    {
        label: 'Orders',
        description: 'Distinct visitors whose website session is marked as having placed an order in the selected date range.',
    },
];

const profileFunnelDefinitions: FunnelDefinition[] = [
    {
        label: 'Visited',
        description: 'Distinct website visitors who viewed at least one page in the selected date range.',
    },
    {
        label: 'Registered',
        description: 'Customers who registered with a mobile number in the selected date range.',
    },
    {
        label: 'Updated Profile',
        description: 'Registered customers who replaced the temporary profile name with real profile details.',
    },
];

const FunnelDefinitionPanel = ({
    title,
    definitions,
    note,
}: {
    title: string;
    definitions: FunnelDefinition[];
    note: string;
}) => (
    <Box
        sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            padding: 2,
            marginBottom: 2,
            backgroundColor: 'background.default',
        }}
    >
        <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            {title}
        </Typography>
        <Grid container spacing={2}>
            {definitions.map(item => (
                <Grid item xs={12} sm={6} md={3} key={item.label}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {item.description}
                    </Typography>
                </Grid>
            ))}
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1.5 }}>
            {note}
        </Typography>
    </Box>
);

const DashboardFunnels = () => {
    useAuthorize(MARKETING_DASHBOARD_ROLES);

    // Default date range: first day of the current year through today.
    const getDefaultDateRange = () => {
        const now = new Date();
        const startDate = startOfYear(now);

        return {
            fromDate: format(startDate, 'yyyy-MM-dd'),
            toDate: format(now, 'yyyy-MM-dd'),
        };
    };

    const defaultDates = getDefaultDateRange();

    const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly'>('monthly');
    const [fromDate, setFromDate] = useState<string>(defaultDates.fromDate);
    const [toDate, setToDate] = useState<string>(defaultDates.toDate);
    const [quickRange, setQuickRange] = useState<string>('');
    const [utmSource, setUtmSource] = useState<string>('');
    const [utmMedium, setUtmMedium] = useState<string>('');
    const [utmCampaign, setUtmCampaign] = useState<string>('');

    const [buckets, setBuckets] = useState<any[]>([]);
    const [totals, setTotals] = useState<CommerceFunnelTotals>(emptyCommerceFunnelTotals);
    const [profileBuckets, setProfileBuckets] = useState<any[]>([]);
    const [profileTotals, setProfileTotals] = useState<{ visits: number; registered: number; updated_profile: number }>({
        visits: 0,
        registered: 0,
        updated_profile: 0,
    });
    const [loading, setLoading] = useState(true);
    const [dataThrough, setDataThrough] = useState<string | null>(null);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
    const [utmOptions, setUtmOptions] = useState<{
        sources: Array<{ value: string; count: number }>;
        mediums: Array<{ value: string; count: number }>;
        campaigns: Array<{ value: string; count: number }>;
    }>({ sources: [], mediums: [], campaigns: [] });

    useEffect(() => {
        const fetchUtmOptions = async () => {
            try {
                const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '/');
                const apiUrl = `${apiBase}customers/utm-values`;
                console.log('📥 Fetching UTM options from:', apiUrl);
                console.log('📥 API Base URL:', apiBase);
                
                const response = await axios.get(apiUrl);
                console.log('✅ UTM options received:', {
                    sources: response.data.utm_sources?.length || 0,
                    mediums: response.data.utm_mediums?.length || 0,
                    campaigns: response.data.utm_campaigns?.length || 0,
                    sampleSources: response.data.utm_sources?.slice(0, 3).map((s: any) => s.value),
                    sampleMediums: response.data.utm_mediums?.slice(0, 3).map((s: any) => s.value),
                    sampleCampaigns: response.data.utm_campaigns?.slice(0, 3).map((s: any) => s.value),
                    fullData: response.data
                });
                
                const options = {
                    sources: response.data.utm_sources || [],
                    mediums: response.data.utm_mediums || [],
                    campaigns: response.data.utm_campaigns || []
                };
                
                console.log('📊 Setting UTM options:', {
                    sourcesCount: options.sources.length,
                    mediumsCount: options.mediums.length,
                    campaignsCount: options.campaigns.length
                });
                
                setUtmOptions(options);
            } catch (error: any) {
                console.error('❌ Error fetching UTM options:', error);
                console.error('❌ Error response:', error.response);
                console.error('❌ Error details:', error.response?.data || error.message);
                console.error('❌ Error stack:', error.stack);
                // Set empty options on error so UI doesn't break
                setUtmOptions({ sources: [], mediums: [], campaigns: [] });
            }
        };
        fetchUtmOptions();
    }, []);

    useEffect(() => {
        const fetchFunnels = async () => {
            setLoading(true);
            const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '/');
            const apiUrl = `${apiBase}customers/funnels`;
            const params = {
                period,
                from_date: fromDate,
                to_date: toDate,
                utm_source: utmSource || undefined,
                utm_medium: utmMedium || undefined,
                utm_campaign: utmCampaign || undefined,
            };
            
            // Log what we're sending to help debug
            console.log('📤 Sending Funnels API request:', {
                url: apiUrl,
                params: params,
                utmSource: utmSource || '(empty)',
                utmMedium: utmMedium || '(empty)',
                utmCampaign: utmCampaign || '(empty)'
            });
            
            try {
                const response = await axios.get(apiUrl, { params });
                setBuckets(response.data?.buckets || []);
                setTotals({ ...emptyCommerceFunnelTotals, ...(response.data?.totals || {}) });
                setProfileBuckets(response.data?.profileBuckets || []);
                setProfileTotals(response.data?.profileTotals || { visits: 0, registered: 0, updated_profile: 0 });
                setDataThrough(response.data?.data_through || response.headers?.['x-analytics-data-through'] || null);
                setLastUpdatedAt(response.data?.last_updated_at || response.headers?.['x-analytics-last-updated'] || null);
            } catch (error: any) {
                console.error('❌ Error fetching funnels:', error);
                setBuckets([]);
                setTotals(emptyCommerceFunnelTotals);
                setProfileBuckets([]);
                setProfileTotals({ visits: 0, registered: 0, updated_profile: 0 });
                setDataThrough(null);
                setLastUpdatedAt(null);
            } finally {
                setLoading(false);
            }
        };
        fetchFunnels();
    }, [period, fromDate, toDate, utmSource, utmMedium, utmCampaign]);

    const handlePeriodChange = (event: any) => {
        setPeriod(event.target.value);
    };

    const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFromDate(event.target.value);
        setQuickRange(''); // Reset quick range when manually changing dates
    };

    const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setToDate(event.target.value);
        setQuickRange(''); // Reset quick range when manually changing dates
    };

    const handleQuickRangeChange = (event: any) => {
        const range = event.target.value;
        setQuickRange(range);
        
        if (!range) {
            return; // Don't update dates if "Custom" is selected
        }

        const now = new Date();
        let from = now;
        let to = now;

        switch (range) {
            case 'today':
                from = to = now;
                break;
            case 'yesterday':
                from = to = subDays(now, 1);
                break;
            case 'last7':
                from = subDays(now, 6);
                to = now;
                break;
            case 'thisWeek':
                from = startOfWeek(now, { weekStartsOn: 1 }); // Monday start
                to = endOfWeek(now, { weekStartsOn: 1 });
                break;
            case 'thisMonth':
                from = startOfMonth(now);
                to = endOfMonth(now);
                break;
            default:
                break;
        }

        setFromDate(format(from, 'yyyy-MM-dd'));
        setToDate(format(to, 'yyyy-MM-dd'));
    };

    const pct = (num: number, den: number) => {
        if (!den || den === 0) return '0%';
        return `${((num / den) * 100).toFixed(1)}%`;
    };

    const inquiriesRateText = totals.registered > 0
        ? `${pct(totals.inquiries, totals.registered)} from registered`
        : `${pct(totals.inquiries, totals.visits)} from visits`;

    const ordersRateText = totals.registered > 0
        ? `${pct(totals.orders, totals.registered)} from registered`
        : `${pct(totals.orders, totals.visits)} from visits`;

    return (
        <Box sx={{ padding: 2 }}>
            <Card>
                <CardHeader
                    title="Visitor to Order Funnel"
                    subheader="Shows how many distinct people reached each major buying and inquiry step during the selected date range."
                />
                <CardContent>
                    <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Period</InputLabel>
                                <Select value={period} onChange={handlePeriodChange} label="Period">
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Quick Range</InputLabel>
                                <Select value={quickRange} onChange={handleQuickRangeChange} label="Quick Range">
                                    <MenuItem value="">Custom</MenuItem>
                                    <MenuItem value="today">Today</MenuItem>
                                    <MenuItem value="yesterday">Yesterday</MenuItem>
                                    <MenuItem value="last7">Last 7 Days</MenuItem>
                                    <MenuItem value="thisWeek">This Week</MenuItem>
                                    <MenuItem value="thisMonth">This Month</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Autocomplete
                                freeSolo
                                options={utmOptions.sources.map(opt => opt.value)}
                                value={utmSource}
                                onChange={(event, newValue) => setUtmSource(newValue || '')}
                                onInputChange={(event, newInputValue) => setUtmSource(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="UTM Source"
                                        placeholder="facebook, google, direct..."
                                        helperText={utmSource ? `Filtering by: ${utmSource}` : `Select or type UTM source (${utmOptions.sources.length} available)`}
                                        size="small"
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const opt = utmOptions.sources.find(o => o.value === option);
                                    return (
                                        <li {...props} key={option}>
                                            {option} {opt && `(${opt.count} visitors)`}
                                        </li>
                                    );
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Autocomplete
                                freeSolo
                                options={utmOptions.mediums.map(opt => opt.value)}
                                value={utmMedium}
                                onChange={(event, newValue) => setUtmMedium(newValue || '')}
                                onInputChange={(event, newInputValue) => setUtmMedium(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="UTM Medium"
                                        placeholder="cpc, email, social..."
                                        helperText={utmMedium ? `Filtering by: ${utmMedium}` : `Select or type UTM medium (${utmOptions.mediums.length} available)`}
                                        size="small"
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const opt = utmOptions.mediums.find(o => o.value === option);
                                    return (
                                        <li {...props} key={option}>
                                            {option} {opt && `(${opt.count} visitors)`}
                                        </li>
                                    );
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Autocomplete
                                freeSolo
                                options={utmOptions.campaigns.map(opt => opt.value)}
                                value={utmCampaign}
                                onChange={(event, newValue) => setUtmCampaign(newValue || '')}
                                onInputChange={(event, newInputValue) => setUtmCampaign(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="UTM Campaign"
                                        placeholder="campaign name"
                                        helperText={utmCampaign ? `Filtering by: ${utmCampaign}` : `Select or type UTM campaign (${utmOptions.campaigns.length} available)`}
                                        size="small"
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const opt = utmOptions.campaigns.find(o => o.value === option);
                                    return (
                                        <li {...props} key={option}>
                                            {option} {opt && `(${opt.count} visitors)`}
                                        </li>
                                    );
                                }}
                            />
                        </Grid>
                    </Grid>
                    {dataThrough && (
                        <AnalyticsFreshnessNote dataThrough={dataThrough} lastUpdatedAt={lastUpdatedAt} />
                    )}

                    <FunnelDefinitionPanel
                        title="What this funnel counts"
                        definitions={commerceFunnelDefinitions}
                        note="The total view counts each step by the date that action happened. The period view shows the same visits, wishlist actions, registrations, inquiries, and orders split into the selected daily, weekly, monthly, or quarterly buckets."
                    />

                    {loading ? (
                        <Skeleton height={120} />
                    ) : (
                        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                            <Grid item xs={12} sm={6} md>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2">Visits (distinct visitors)</Typography>
                                        <Typography variant="h5">{totals.visits}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            People who viewed the website
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2">Wishlist</Typography>
                                        <Typography variant="h5">{totals.wishlist}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {pct(totals.wishlist, totals.visits)} of visitors saved a product
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2">Registered</Typography>
                                        <Typography variant="h5">{totals.registered}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {pct(totals.registered, totals.visits)} of visitors registered
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2">Inquiry</Typography>
                                        <Typography variant="h5">{totals.inquiries}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {inquiriesRateText}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle2">Orders</Typography>
                                        <Typography variant="h5">{totals.orders}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {ordersRateText}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}

                    {loading ? (
                        <Skeleton height={320} />
                    ) : buckets.length === 0 ? (
                        <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No funnel data available for the selected date range</p>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={5}>
                                <Card variant="outlined">
                                    <CardHeader
                                        title="Funnel Totals"
                                        subheader="One total number for each step across the full selected date range."
                                    />
                                    <CardContent>
                                        <FunnelSummaryChart totals={totals} />
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <Card variant="outlined">
                                    <CardHeader
                                        title="Funnel by Period"
                                        subheader="Shows whether visits, wishlist activity, registrations, inquiries, and orders are rising or falling over time."
                                    />
                                    <CardContent>
                                        <FunnelTrendChart data={buckets} period={period} />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
            </Card>

            <Card sx={{ marginTop: 3 }}>
                <CardHeader
                    title="Customer Profile Funnel"
                    subheader="Shows how people move from visiting the website to registering, then completing their customer profile."
                />
                <CardContent>
                    <FunnelDefinitionPanel
                        title="What this funnel counts"
                        definitions={profileFunnelDefinitions}
                        note="This funnel is focused on customer identity quality. Updated Profile means the customer no longer has the temporary user-style profile name."
                    />

                    {loading ? (
                        <Skeleton height={120} />
                    ) : (
                        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                            <Grid item xs={12} sm={4}>
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, padding: 2 }}>
                                    <Typography variant="subtitle2">Visited</Typography>
                                    <Typography variant="h5">{profileTotals.visits}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        People who viewed the website
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, padding: 2 }}>
                                    <Typography variant="subtitle2">Registered</Typography>
                                    <Typography variant="h5">{profileTotals.registered}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {pct(profileTotals.registered, profileTotals.visits)} of visitors registered
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, padding: 2 }}>
                                    <Typography variant="subtitle2">Updated Profile</Typography>
                                    <Typography variant="h5">{profileTotals.updated_profile}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {pct(profileTotals.updated_profile, profileTotals.registered)} of registered customers updated details
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    {loading ? (
                        <Skeleton height={360} />
                    ) : profileBuckets.length === 0 ? (
                        <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p>No customer profile funnel data available for the selected date range</p>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={5}>
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, padding: 2 }}>
                                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                        Funnel Totals
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                                        One total number for each profile step across the full selected date range.
                                    </Typography>
                                    <CustomerProfileFunnelSummaryChart totals={profileTotals} />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, padding: 2 }}>
                                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                        Funnel by Period
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                                        Shows whether registrations and profile completions are improving over time.
                                    </Typography>
                                    <CustomerProfileFunnelTrendChart data={profileBuckets} period={period} />
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default DashboardFunnels;
